// In src/pages/Directory.js

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
// MODIFIED: Added getDoc for fetching private contact info
import { collection, query, where, getDocs, orderBy as firestoreOrderBy, doc, getDoc } from 'firebase/firestore'; 

// Import Material-UI components
import {
  List, ListItem, ListItemText, ListItemAvatar, Avatar,
  Container, Typography, CircularProgress, Divider, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Link
} from '@mui/material';
import format from 'date-fns/format';

function Directory() {
  const { currentUser, userProfile } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isContactCardOpen, setIsContactCardOpen] = useState(false);
  // MODIFIED: selectedMember will now hold combined data after fetch
  const [selectedMember, setSelectedMember] = useState(null); 

  // isLeadershipRole determines who can see sensitive details
  const isLeadershipRole = userProfile?.role === 'Leadership' || userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      return; 
    }

    const fetchMembers = async () => {
      setLoading(true);
      try {
        const usersColRef = collection(db, 'users');
        // Fetch ALL active members from the current user's church.
        // This query only fetches the main user document (non-sensitive fields, including profilePhotoUrl).
        const q = query(
          usersColRef,
          where('churchId', '==', userProfile.churchId),
          where('status', '==', 'active'), 
          firestoreOrderBy('lastName'),
          firestoreOrderBy('firstName')
        );
        const querySnapshot = await getDocs(q);

        const memberList = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() })) // Store basic data (now includes profilePhotoUrl)
          .filter(member => member.authUid !== currentUser.uid)
          .filter(member => member.role !== 'Super Administrator');

        setMembers(memberList);
      } catch (error) {
        console.error("Error fetching members:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [userProfile, currentUser.uid]);


  if (userProfile === undefined || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userProfile?.churchId) {
    return <Navigate to="/join-church" />;
  }


  const handleMemberClick = async (member) => {
    // MODIFIED: Remove alert for non-leadership roles, just return
    if (isLeadershipRole) {
      // NEW: Fetch sensitive contact info (including email) from subcollection
      const privateContactInfoDocRef = doc(db, 'users', member.id, 'privateContactInfo', 'details');
      try {
        const privateDocSnap = await getDoc(privateContactInfoDocRef);
        let sensitiveData = {};
        if (privateDocSnap.exists()) {
          sensitiveData = privateDocSnap.data();
        }
        // Combine basic member data with sensitive data for the modal
        setSelectedMember({ ...member, ...sensitiveData });
        setIsContactCardOpen(true);
      } catch (error) {
        console.error("Error fetching sensitive contact info:", error);
        // If there's an error fetching sensitive data, still open modal but with only public info
        setSelectedMember(member); // Set member with only public data available
        setIsContactCardOpen(true);
      }
    } else {
      // For non-leadership roles, do nothing when they click.
      // The list item remains clickable, but no action is taken.
      return; 
    }
  };

  const handleCloseContactCard = () => {
    setIsContactCardOpen(false);
    setSelectedMember(null);
  };

  const formatBirthday = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM do, yyyy');
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom>Member Directory</Typography>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {members.length === 0 ? (
          <Typography>No other members found in this church.</Typography>
        ) : (
          members.map((member, index) => (
            <React.Fragment key={member.authUid}>
              <ListItem
                onClick={() => handleMemberClick(member)}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemAvatar>
                  {/* MODIFIED: Display profile photo if available, otherwise initials */}
                  <Avatar src={member.profilePhotoUrl || ''} alt={`${member.firstName} ${member.lastName}`}>
                    {!member.profilePhotoUrl && member.firstName.charAt(0) + member.lastName.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${member.firstName} ${member.lastName}`}
                  // MODIFIED: Removed email from secondary text in the list view
                  // secondary={member.email} 
                />
              </ListItem>
              {index < members.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))
        )}
      </List>

      <Dialog open={isContactCardOpen} onClose={handleCloseContactCard}>
        <DialogTitle>Contact: {selectedMember?.firstName} {selectedMember?.lastName}</DialogTitle>
        <DialogContent>
          {selectedMember && (
            <Box>
              {/* MODIFIED: Display profile photo in contact card */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar 
                  src={selectedMember.profilePhotoUrl || ''} 
                  alt={`${selectedMember.firstName} ${selectedMember.lastName}`} 
                  sx={{ width: 80, height: 80 }} 
                >
                  {!selectedMember.profilePhotoUrl && selectedMember.firstName.charAt(0) + selectedMember.lastName.charAt(0)}
                </Avatar>
              </Box>

              {/* MODIFIED: Display email from selectedMember, which now comes from privateContactInfo */}
              {selectedMember.email && <Typography variant="body1"><strong>Email:</strong> {selectedMember.email}</Typography>}
              {selectedMember.phone && (
                <Typography variant="body1">
                  <strong>Phone:</strong>{' '}
                  <Link href={`tel:${selectedMember.phone}`} target="_blank" rel="noopener">
                    {selectedMember.phone}
                  </Link>
                </Typography>
              )}
              {selectedMember.address && (
                <Typography variant="body1"><strong>Address:</strong> {selectedMember.address}</Typography>
              )}
              {selectedMember.city && (
                  <Typography variant="body1"><strong>City:</strong> {selectedMember.city}</Typography>
              )}
              {selectedMember.state && (
                  <Typography variant="body1"><strong>State:</strong> {selectedMember.state}</Typography>
              )}
              {selectedMember.zip && (
                  <Typography variant="body1"><strong>Zip Code:</strong> {selectedMember.zip}</Typography>
              )}
              <Typography variant="body1"><strong>Birthday:</strong> {formatBirthday(selectedMember.birthday)}</Typography>
              {/* Always show role, as it's not sensitive */}
              <Typography variant="body1"><strong>Role:</strong> {selectedMember.role || 'Member'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactCard}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Directory;